/**
 * bus.js — lightweight application-level pub/sub
 *
 * Built on the native EventTarget so there are no external dependencies and
 * the browser handles memory/GC automatically.  Every module can import
 * { emit, on, off } without pulling in script.js, which is exactly what
 * breaks the circular-dependency chains that currently keep large sections
 * of code locked inside script.js.
 *
 * Usage:
 *   import { emit, on, off, BUS_EVENTS } from './bus.js';
 *
 *   // Subscribe (e.g. in the renderer module):
 *   on(BUS_EVENTS.SITE_LIST_CHANGED, () => renderSiteList());
 *
 *   // Emit (e.g. after a mutation in the palette module):
 *   emit(BUS_EVENTS.SITE_LIST_CHANGED);
 *
 *   // Emit with data:
 *   emit(BUS_EVENTS.WORKLOAD_CHANGED, { siteId: 3 });
 *
 *   // Receive data in the handler:
 *   on(BUS_EVENTS.WORKLOAD_CHANGED, (e) => console.log(e.detail.siteId));
 *
 *   // Unsubscribe when cleaning up:
 *   off(BUS_EVENTS.ESTIMATE_NEEDED, myHandler);
 */

const _bus = new EventTarget();

/**
 * Publish an event, optionally carrying a detail payload.
 * @param {string} type  - one of BUS_EVENTS
 * @param {object} [detail={}] - arbitrary data for subscribers
 */
export function emit(type, detail = {}) {
  _bus.dispatchEvent(new CustomEvent(type, { detail }));
}

/**
 * Subscribe to an event.  The handler receives a CustomEvent; data is on
 * handler.detail.
 * @param {string}   type
 * @param {Function} handler
 */
export function on(type, handler) {
  _bus.addEventListener(type, handler);
}

/**
 * Unsubscribe a previously registered handler.
 * @param {string}   type
 * @param {Function} handler
 */
export function off(type, handler) {
  _bus.removeEventListener(type, handler);
}

/**
 * BUS_EVENTS — centralised event-name constants.
 *
 * Using constants instead of raw strings means a typo fails loudly at
 * import time (undefined property) rather than silently at runtime.
 *
 * Add new event names here as modules are extracted from script.js.
 */
export const BUS_EVENTS = {
  /** One or more sites were added, removed, cloned, or reordered.
   *  Subscribers should call renderSiteList(). */
  SITE_LIST_CHANGED: 'site-list-changed',

  /** A workload was added, removed, or its quantity/type changed on a site.
   *  detail: { siteId? } */
  WORKLOAD_CHANGED: 'workload-changed',

  /** The current step changed (1 → 2, 2 → 3, etc.).
   *  detail: { step: number } */
  STEP_CHANGED: 'step-changed',

  /** Something mutated state that affects the effort estimate.
   *  Subscribers should call scheduleRenderEstimate(). */
  ESTIMATE_NEEDED: 'estimate-needed',

  /** The palette workload selection changed.
   *  detail: { workloadValue: string, selected: boolean } */
  PALETTE_CHANGED: 'palette-changed',

  /** A named Veeam server was added, removed, or updated.
   *  detail: { siteId: number } */
  SERVER_CHANGED: 'server-changed',

  /** Step 2 canvas lanes or workload assignments changed. */
  STEP2_CANVAS_CHANGED: 'step2-canvas-changed',

  /** Trigger syncDesignOptionFields() — emitted by extracted modules instead of calling the
   *  function directly (avoids importing from script.js). */
  DESIGN_SYNC_NEEDED: 'design-sync-needed',

  /** Trigger updateWorkloadValidationState() + syncNextStepAvailability() together.
   *  The two are always paired in mutation handlers so one event covers both. */
  VALIDATION_SYNC_NEEDED: 'validation-sync-needed',

  /** Trigger updateTotalInstances() + updateGfSummaryCount() + updateNonGfNameBarCount().
   *  Emitted after any mutation that changes workload/job/component counts. */
  COUNTS_SYNC_NEEDED: 'counts-sync-needed',
};
